"use strict";
/**
 * Moteur d'évaluation de définition d'enquête.
 *
 * Fonction PURE et déterministe. Appelée côté client pour piloter le rendu
 * et l'autosave, et côté serveur pour re-valider la soumission. Le serveur
 * est la source de vérité ; les réponses aux items NON visibles sont
 * REJETÉES (minimisation — RGPD_PLAN §2).
 *
 * Voir app/docs/SURVEY_ENGINE_SPEC.md.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.findItem = findItem;
exports.evaluateSurvey = evaluateSurvey;
exports.sanitizeAnswers = sanitizeAnswers;
exports.buildPayload = buildPayload;
const expr_1 = require("./expr");
/** Détermine si un item est visible (passe son `relevant` + celui de sa section/block). */
function isVisible(item, section, block, answers) {
    if (block.relevant && !(0, expr_1.evaluateBool)(block.relevant, answers))
        return false;
    if (section?.relevant && !(0, expr_1.evaluateBool)(section.relevant, answers))
        return false;
    if (item.relevant && !(0, expr_1.evaluateBool)(item.relevant, answers))
        return false;
    return true;
}
function isAnswered(item, value) {
    if (value === undefined || value === null)
        return false;
    if (Array.isArray(value))
        return value.length > 0;
    if (typeof value === 'string')
        return value.length > 0;
    if (typeof value === 'object')
        return Object.keys(value).length > 0;
    return true;
}
function* walkItems(def) {
    for (const block of def.blocks) {
        if (block.items) {
            for (const item of block.items)
                yield { block, item };
        }
        if (block.sections) {
            for (const section of block.sections) {
                for (const item of section.items)
                    yield { block, section, item };
            }
        }
    }
}
/** Trouve un item par name (utilitaire). */
function findItem(def, name) {
    for (const { item } of walkItems(def))
        if (item.name === name)
            return item;
    return undefined;
}
/**
 * Valide un item visible. Retourne 0..n erreurs.
 * - required
 * - constraint
 * - exclusive (select_multiple)
 * - range (integer)
 */
function validateItem(item, value, answers) {
    const out = [];
    if (item.type === 'note')
        return out; // jamais d'erreur sur les notes
    const answered = isAnswered(item, value);
    if (item.required && !answered) {
        out.push({ itemName: item.name, code: 'required', message: 'required' });
        // ne pas valider plus loin : pas de valeur
        return out;
    }
    if (!answered)
        return out;
    if (item.type === 'select_multiple') {
        const arr = (Array.isArray(value) ? value : []);
        if (item.exclusive && item.exclusive.length > 0) {
            const exclusivesPresent = arr.filter((v) => item.exclusive.includes(v));
            if (exclusivesPresent.length > 0 && arr.length !== 1) {
                out.push({
                    itemName: item.name,
                    code: 'exclusive',
                    message: `Option exclusive "${exclusivesPresent[0]}" — désélectionner les autres`,
                });
            }
        }
        const allowed = new Set(item.choices.map((c) => c.value));
        for (const v of arr) {
            if (!allowed.has(v)) {
                out.push({ itemName: item.name, code: 'type', message: `Choix inconnu: ${v}` });
            }
        }
    }
    if (item.type === 'select_one') {
        if (typeof value !== 'string') {
            out.push({ itemName: item.name, code: 'type', message: 'attendu string' });
        }
        else {
            const allowed = new Set(item.choices.map((c) => c.value));
            if (!allowed.has(value)) {
                out.push({ itemName: item.name, code: 'type', message: `Choix inconnu: ${value}` });
            }
        }
    }
    if (item.type === 'integer') {
        if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value)) {
            out.push({ itemName: item.name, code: 'type', message: 'attendu entier' });
        }
        else {
            if (item.min !== undefined && value < item.min) {
                out.push({ itemName: item.name, code: 'range', message: `min ${item.min}` });
            }
            if (item.max !== undefined && value > item.max) {
                out.push({ itemName: item.name, code: 'range', message: `max ${item.max}` });
            }
        }
    }
    if (item.type === 'text_short' || item.type === 'text_long') {
        if (typeof value !== 'string') {
            out.push({ itemName: item.name, code: 'type', message: 'attendu string' });
        }
        else if (item.maxLength && value.length > item.maxLength) {
            out.push({ itemName: item.name, code: 'range', message: `maxLength ${item.maxLength}` });
        }
    }
    if (item.type === 'matrix') {
        if (typeof value !== 'object' || Array.isArray(value) || value === null) {
            out.push({ itemName: item.name, code: 'type', message: 'attendu object' });
        }
        else {
            const allowed = new Set(item.choices.map((c) => c.value));
            const rowNames = new Set(item.rows.map((r) => r.name));
            for (const [row, val] of Object.entries(value)) {
                if (!rowNames.has(row)) {
                    out.push({ itemName: item.name, code: 'type', message: `Ligne inconnue: ${row}` });
                }
                if (!allowed.has(val)) {
                    out.push({ itemName: item.name, code: 'type', message: `Valeur inconnue pour ${row}: ${val}` });
                }
            }
        }
    }
    if (item.constraint) {
        try {
            const ok = (0, expr_1.evaluateBool)(item.constraint, { ...answers, [item.name]: value });
            if (!ok) {
                out.push({
                    itemName: item.name,
                    code: 'constraint',
                    message: item.constraintMessage ?? 'constraint failed',
                });
            }
        }
        catch (e) {
            out.push({
                itemName: item.name,
                code: 'expression',
                message: e instanceof expr_1.ExpressionError ? e.message : String(e),
            });
        }
    }
    return out;
}
function computeScale(scale, answers) {
    const range = scale.range ?? [1, 5];
    const missing = new Set(scale.missing ?? []);
    const reverse = new Set(scale.reverse ?? []);
    let sum = 0;
    let answered = 0;
    for (const name of scale.items) {
        const v = answers[name];
        if (v === null || v === undefined)
            continue;
        if (typeof v === 'string' && missing.has(v))
            continue;
        const n = typeof v === 'number' ? v : parseFloat(String(v));
        if (!Number.isFinite(n))
            continue;
        const score = reverse.has(name) ? range[0] + range[1] - n : n;
        sum += score;
        answered++;
    }
    const total = scale.items.length;
    const ratio = answered / Math.max(1, total);
    const min = scale.minItemsRequired ?? 0;
    const computed = ratio >= min ? sum : null;
    return {
        scaleId: scale.id,
        score: computed,
        itemsAnswered: answered,
        itemsTotal: total,
        hidden: Boolean(scale.hidden),
    };
}
/**
 * Évalue l'état complet d'une définition contre des réponses.
 * - Ne valide PAS les items non visibles.
 * - Détecte la fin par consentement refusé (`endSurveyOn`).
 * - Calcule les échelles (`scales`).
 */
function evaluateSurvey(def, answers) {
    const visibleItems = [];
    const missingRequired = [];
    const errors = [];
    // 1. Vérifier d'abord le consentement (court-circuit éventuel)
    let end = false;
    let endReason;
    if (def.consentItem) {
        const item = findItem(def, def.consentItem);
        if (item && item.type === 'select_one' && item.endSurveyOn) {
            const v = answers[def.consentItem];
            if (typeof v === 'string' && v === item.endSurveyOn) {
                end = true;
                endReason = 'consent_refused';
            }
        }
    }
    for (const { item, section, block } of walkItems(def)) {
        if (!isVisible(item, section, block, answers))
            continue;
        visibleItems.push(item.name);
        if (item.type === 'note')
            continue;
        const value = answers[item.name];
        if (item.required && !isAnswered(item, value)) {
            missingRequired.push(item.name);
        }
        if (value !== undefined && value !== null) {
            const errs = validateItem(item, value, answers);
            errors.push(...errs);
        }
    }
    const scores = (def.scales ?? []).map((s) => computeScale(s, answers));
    // Si fin par consentement, "complet" est faux (pas de soumission).
    const isComplete = !end && missingRequired.length === 0 && errors.length === 0;
    const state = {
        visibleItems,
        missingRequired,
        errors,
        scores,
        isComplete,
        end,
    };
    if (endReason !== undefined)
        state.endReason = endReason;
    return state;
}
/**
 * Filtre un payload soumis pour ne garder que les réponses aux items
 * VISIBLES. Garantit la minimisation côté serveur — JAMAIS stocker les
 * réponses à des questions masquées.
 */
function sanitizeAnswers(def, answers) {
    const state = evaluateSurvey(def, answers);
    const visible = new Set(state.visibleItems);
    const out = {};
    for (const [k, v] of Object.entries(answers)) {
        if (visible.has(k))
            out[k] = v;
    }
    return out;
}
/**
 * Crée un payload moteur séparé (answers, identity) en respectant le mode.
 * - anonymous : identity = undefined, jamais émis.
 * - optional + identity provided : identity séparée (non rebindable).
 * - optional + no identity : identity = undefined (anti-rebinding).
 * - required : identity obligatoire.
 *
 * Cette fonction NE STOCKE PAS — elle prépare le payload que le backend
 * écrit dans deux tables séparées (ResponseRecord vs RespondentIdentity).
 */
function buildPayload(def, answers, identity = null) {
    const sanitized = sanitizeAnswers(def, answers);
    const payload = { answers: sanitized };
    if (def.identityMode === 'anonymous') {
        // jamais d'identité — ignorer silencieusement toute identité fournie
        return payload;
    }
    if (def.identityMode === 'required') {
        if (!identity || Object.keys(identity).length === 0) {
            // L'absence d'identité en required produira une erreur à la validation
            // côté serveur ; le payload reste sans identité plutôt que d'inventer.
            return payload;
        }
        payload.identity = identity;
        return payload;
    }
    // optional
    if (identity && Object.keys(identity).length > 0) {
        payload.identity = identity;
    }
    return payload;
}
//# sourceMappingURL=engine.js.map